import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Skeleton } from '@material-ui/lab';
import { HierarchyControlItem, HierarchyControlProps, MultiHierarchyControlProps, SingleHierarchyControlProps } from './HierarchyControl.types';
import { Hierarchy } from './models/Hierarchy';
import { useInputValue } from './hooks/useInputValue';
import { Input } from './components/Input';
import { Popover } from './components/Popover';
import { Search } from './components/Search';
import { Tree, TreeItem } from './components/Tree';
import { Scroll } from './components/Scroll';
import cn from 'classnames';

export function HierarchyControl<T>(props: MultiHierarchyControlProps<T> | SingleHierarchyControlProps<T>) {
  const { items, label, value, large, inputLarge, id, className, loading, onChange, renderInputValue } = props;
  const noSelectableParent = props.noSelectableParent || false;
  const multiple = (props as MultiHierarchyControlProps<T>).multiple || false;

  const [searchQuery, setSearchQuery] = useState("");
  const [selected, setSelected] = useState<T | T[]>(value);
  const [anchorEl, setAnchorEl] = useState<Element | undefined>();
  const ref = useRef(null);

  useEffect(() => {
    if (value !== selected) {
      setSelected(value);
    }
  }, [value]);

  const filtredItems = useMemo(() => {
    const normolize = (str : string) => str?.trim().toLowerCase() || '';
    const q = normolize(searchQuery);
    return items.filter(e => normolize(e.name).includes(q));
  }, [items, searchQuery]);

  const hierarchies = useMemo(() => Hierarchy.from<T, HierarchyControlItem<T>>(filtredItems), [filtredItems]);
  const itemsForInput = noSelectableParent ? items.filter(item => items.every(e => e.parent !== item.id)) : items;
  const inputValue = useInputValue(selected, itemsForInput, renderInputValue);
  const handlerOpenSelector = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setAnchorEl(e.currentTarget as Element);
    setTimeout(() => {
      const selectedItem = (ref.current || document.querySelector('.hierarchy-control')!).querySelector('[aria-selected="true"]');
      try {
        // For IE. block start
        selectedItem?.scrollIntoView();
        // For NO IE. block center
        selectedItem?.scrollIntoView({
          block: 'end',
        });
      } catch (e) {

      }
    }, 100);
  }, []);
  const handlerCloseSelector = useCallback(() => {
    if (multiple) {
      onChange(selected as any);
    }
    setAnchorEl(undefined);
    setTimeout(() => setSearchQuery(''), 300);
  }, [selected]);

  const handlerNodeSelect = useCallback((event: React.ChangeEvent<{}>, nodeIds: T | T[]) => {
    setSelected(nodeIds);

    if (!multiple) {
      onChange(nodeIds as any);
      handlerCloseSelector();
    }
  }, [multiple, onChange]);

  const loader = React.useMemo(() => {
    return <Skeleton variant="rect" width="168px" height="51px" />;
  }, []);

  const expanded = React.useMemo(() => {
    const ids = multiple ? (value as T[]) : [value as T];
    const parentsHierarchies = ids.map(id => hierarchies.find(h => h.includes(id)));
    const candidates = parentsHierarchies.flatMap(e => e?.flatChildren).filter(Boolean) as Hierarchy<T, HierarchyControlItem<T>>[];
    const selectedHierarchies = ids.map(id => candidates.find(e => e.id === id));
    const expandedIds = selectedHierarchies.flatMap(h => h?.parents.flatMap(p => p.id));
    return expandedIds.filter(Boolean) as T[];
  }, [value, hierarchies]);

  if (loading) {
    return loader;
  }
  return <div id={id} className={cn('hierarchy-control', className)}>
    <Input label={label} inputLarge={inputLarge} value={inputValue} open={Boolean(anchorEl)} onOpenClick={handlerOpenSelector} />
    <Popover anchorEl={anchorEl} onClose={handlerCloseSelector} large={large}>
      <Search onSearch={setSearchQuery} total={items.length} debounce />
      <Scroll ref={ref} maxHeight={large ? '308px' : '248px'}>
        <Tree
          multiple={multiple as any}
          selected={selected as any}
          selectableParent={!noSelectableParent}
          onSelect={handlerNodeSelect}
          expanded={expanded}
        >
          {hierarchies.map(h => <TreeItem key={String(h.id)} hierarchy={h} />)}
        </Tree>
      </Scroll>
    </Popover>
  </div>
};